/**
 * Contact Resolution Engine
 * Takes known duplicates and resolves field conflicts using simple priority rules
 *
 * Goals:
 * 1. Apply clear priority rules to resolve field conflicts
 * 2. Generate actionable resolution plans for frontend display
 * 3. Group actions for easy user review and drill-down
 */

import type {
  UnifiedContactGroup,
  ExistingContact,
  UploadContact,
  GroupMemberContact,
} from './duplicate-detector'
import type { ContactUploadContext } from '@/types/csv-upload'
import type {
  ContactResolutionPlan,
  ContactCreateAction,
  ContactPropertyRelationshipRecord,
  ContactUpdateAction,
  ContactSkippedAction,
  CrmContactResolutionAction,
  FieldChange,
  FieldValue,
  FieldResolution,
  RelationshipToCreate,
} from '@super/shared-types'
import {
  isUnknownContactPlaceholder,
  resolveNameParts,
} from '@super/shared-types'
import { getContactDisplayName } from '@/lib/utils/contact-display'

/**
 * Updatable fields configuration
 * Defines which fields can be updated from CSV and how to resolve conflicts
 */
type UpdatableField = keyof Pick<
  ContactPropertyRelationshipRecord,
  | 'firstName'
  | 'lastName'
  | 'primaryEmail'
  | 'primaryPhoneNumber'
  | 'alternateEmails'
  | 'alternatePhoneNumbers'
>

const UPDATABLE_FIELDS: Record<UpdatableField, FieldResolution> = {
  firstName: 'use_new',
  lastName: 'use_new',
  primaryEmail: 'use_new',
  primaryPhoneNumber: 'use_new',
  alternateEmails: 'merge_arrays',
  alternatePhoneNumbers: 'merge_arrays',
} as const

export class ContactResolutionEngine {
  private context: ContactUploadContext

  constructor(context: ContactUploadContext) {
    this.context = context
  }

  /**
   * Generate contact resolution plan from unified groups and unique contacts
   */
  resolveGroups(
    groups: UnifiedContactGroup[],
    uniqueContacts: UploadContact[] = [],
  ): ContactResolutionPlan {
    const newContacts: ContactCreateAction[] = []
    const duplicateCreates: ContactCreateAction[] = []
    const updates: ContactUpdateAction[] = []
    const skipped: ContactSkippedAction[] = []

    // Process unique contacts (no duplicates found)
    for (const contact of uniqueContacts) {
      const relationships: RelationshipToCreate[] = []
      if (
        this.context.relationshipType &&
        (contact.relationshipExternalId ||
          contact.propertyExternalId ||
          contact.unitExternalId)
      ) {
        relationships.push({
          relationshipType: this.context.relationshipType,
          relationshipExternalId: contact.relationshipExternalId,
          propertyExternalId: contact.propertyExternalId,
          unitExternalId: contact.unitExternalId,
          isActive: contact.isActive,
        })
      }

      newContacts.push({
        type: 'create_contact',
        finalContact: {
          ...contact,
          ...resolveNameParts(contact.firstName, contact.lastName),
        },
        relationships,
        isFromDuplicateResolution: false,
      })
    }

    // Process groups (duplicates found)
    for (const group of groups) {
      if (group.operation === 'update') {
        // Generate update action or track as skipped
        const result = this.evaluateUpdateAction(group)
        if (result.action) {
          updates.push(result.action)
        } else if (result.skipped) {
          skipped.push(result.skipped)
        }
      } else {
        // Create action for duplicates within CSV
        const createAction = this.createCreateAction(group)
        duplicateCreates.push(createAction)
      }
    }

    // Generate unified CRM actions for API batching (excludes skipped)
    const crmContactResolutionActions =
      this.generateCrmContactResolutionActions({
        newContacts,
        duplicateCreates,
        updates,
      })

    // Calculate summary statistics
    const fieldUpdatesCount = updates.filter(u => u.hasFieldChanges).length
    const relationshipUpdatesCount = updates.filter(
      u => u.hasRelationshipChanges,
    ).length

    return {
      actionGroups: {
        newContacts,
        duplicateCreates,
        updates,
        skipped,
      },
      crmContactResolutionActions,
      summary: {
        newContactsCount: newContacts.length,
        duplicateCreatesCount: duplicateCreates.length,
        updatesCount: updates.length,
        fieldUpdatesCount,
        relationshipUpdatesCount,
        skippedCount: skipped.length,
        totalActions:
          newContacts.length + duplicateCreates.length + updates.length, // Excludes skipped from actionable total
      },
    }
  }

  /**
   * Evaluate whether to create an update action for an existing contact
   * Returns either an action or a skipped entry
   */
  private evaluateUpdateAction(group: UnifiedContactGroup): {
    action?: ContactUpdateAction
    skipped?: ContactSkippedAction
  } {
    const existingContact = group.existingContact!
    const uploadContacts = group.contacts.filter(c =>
      this.isUploadContact(c),
    ) as UploadContact[]

    // Apply field resolution rules and get merged contact
    const { mergedContact, fieldChanges } = this.applyFieldRules(
      existingContact,
      uploadContacts,
    )

    // Check for new relationships
    const newRelationships = this.findNewRelationships(group)

    // If no field changes and no new relationships, track as skipped
    if (fieldChanges.length === 0 && newRelationships.length === 0) {
      return {
        skipped: {
          type: 'skip_contact',
          targetContactId: existingContact.id,
          targetContactName: getContactDisplayName(existingContact),
          reason: 'No changes detected - contact already up to date',
        },
      }
    }

    // Create an update action with both field and relationship changes
    return {
      action: {
        type: 'update_contact',
        targetContactId: existingContact.id,
        targetContactName: `${existingContact.firstName} ${existingContact.lastName}`,
        finalContact: {
          ...mergedContact,
          ...resolveNameParts(mergedContact.firstName, mergedContact.lastName),
        },
        fieldChanges,
        relationships: newRelationships,
        isFromDuplicateResolution: true,
        hasFieldChanges: fieldChanges.length > 0,
        hasRelationshipChanges: newRelationships.length > 0,
      },
    }
  }

  /**
   * Apply field resolution rules and return merged contact data with changes
   */
  private applyFieldRules(
    existingContact: ExistingContact,
    uploadContacts: UploadContact[],
  ): {
    mergedContact: ContactPropertyRelationshipRecord
    fieldChanges: FieldChange[]
  } {
    const fieldChanges: FieldChange[] = []

    // Treat the Unknown Contact placeholder as absent so a real name from the
    // CSV fully replaces it rather than blending (e.g. "Munchkin Contact").
    const unknownPlaceholder = isUnknownContactPlaceholder(
      existingContact.firstName,
      existingContact.lastName,
    )

    // Start with existing contact data as base
    const mergedContact: ContactPropertyRelationshipRecord = {
      firstName: existingContact.firstName,
      lastName: existingContact.lastName,
      primaryEmail: existingContact.primaryEmail,
      primaryPhoneNumber: existingContact.primaryPhoneNumber,
      alternateEmails: existingContact.alternateEmails,
      alternatePhoneNumbers: existingContact.alternatePhoneNumbers,
      externalId: existingContact.externalId,
      city: existingContact.city,
      aliases: existingContact.aliases,
      relationshipExternalId: undefined,
      propertyExternalId: undefined,
    }

    // Process each upload contact and apply updates
    for (const uploadContact of uploadContacts) {
      // First, merge updatable contact fields (email/phone/name)
      Object.keys(UPDATABLE_FIELDS).forEach(fieldName => {
        const field = fieldName as UpdatableField
        const resolution = UPDATABLE_FIELDS[field]
        const existingValue = mergedContact[field] as FieldValue
        const newValue = uploadContact[field] as FieldValue

        // Skip if no new value provided
        if (newValue === undefined || newValue === '' || newValue === null) {
          return
        }

        const resolvedValue = this.resolveField(
          resolution,
          existingValue,
          newValue,
        )

        // Track and apply changes
        if (this.hasChanged(existingValue, resolvedValue)) {
          fieldChanges.push({
            fieldName: field,
            oldValue: existingValue,
            newValue: resolvedValue,
            resolution,
          })

          mergedContact[field] = resolvedValue as never
        }
      })

      // Second, merge non-contact fields (aliases, tags, city, externalId, companyName)
      this.mergeNonContactFields(mergedContact, uploadContact)
    }

    // If the existing contact was a placeholder, clear any name part the CSV
    // didn't supply and record the change so fieldChanges stays accurate.
    if (unknownPlaceholder) {
      const firstChanged = mergedContact.firstName !== existingContact.firstName
      const lastChanged = mergedContact.lastName !== existingContact.lastName
      if (firstChanged && !lastChanged) {
        fieldChanges.push({
          fieldName: 'lastName',
          oldValue: existingContact.lastName,
          newValue: '',
          resolution: UPDATABLE_FIELDS.lastName,
        })
        mergedContact.lastName = ''
      } else if (!firstChanged && lastChanged) {
        fieldChanges.push({
          fieldName: 'firstName',
          oldValue: existingContact.firstName,
          newValue: '',
          resolution: UPDATABLE_FIELDS.firstName,
        })
        mergedContact.firstName = ''
      }
    }

    return { mergedContact, fieldChanges }
  }

  /**
   * Create a create action for a group of contacts
   * @param group - The group of contacts to create
   * @returns The create action
   */
  private createCreateAction(group: UnifiedContactGroup): ContactCreateAction {
    const uploadContacts = group.contacts.filter(c =>
      this.isUploadContact(c),
    ) as UploadContact[]

    // Use the primary contact as the base
    const baseContact = group.primaryContact as UploadContact

    // Merge all contact data and collect relationships
    const { finalContact, relationships } = this.mergeContactsForCreate(
      baseContact,
      uploadContacts,
    )

    return {
      type: 'create_contact',
      finalContact: {
        ...finalContact,
        ...resolveNameParts(finalContact.firstName, finalContact.lastName),
      },
      relationships,
      isFromDuplicateResolution: true,
    }
  }

  /**
   * Merge multiple upload contacts into a single contact with all relationships
   * @param baseContact - The base contact to merge into
   * @param allContacts - The contacts to merge
   * @returns The final contact and relationships
   */
  private mergeContactsForCreate(
    baseContact: UploadContact,
    allContacts: UploadContact[],
  ): {
    finalContact: UploadContact
    relationships: RelationshipToCreate[]
  } {
    // Start with base contact
    const finalContact = { ...baseContact }

    // Collect all unique data
    const allEmails = new Set<string>()
    const allPhones = new Set<string>()
    const allRelationships: RelationshipToCreate[] = []

    // Process all contacts (including base) to collect data
    for (const contact of allContacts) {
      // Collect emails
      if (contact.primaryEmail) allEmails.add(contact.primaryEmail)
      if (contact.alternateEmails) {
        contact.alternateEmails.forEach(email => allEmails.add(email))
      }

      // Collect phones
      if (contact.primaryPhoneNumber) allPhones.add(contact.primaryPhoneNumber)
      if (contact.alternatePhoneNumbers) {
        contact.alternatePhoneNumbers.forEach(phone => {
          const phoneNumber = typeof phone === 'string' ? phone : phone.number
          allPhones.add(phoneNumber)
        })
      }

      // Collect ALL relationships (no deduplication yet)
      if (
        this.context.relationshipType &&
        (contact.relationshipExternalId ||
          contact.propertyExternalId ||
          contact.unitExternalId)
      ) {
        allRelationships.push({
          relationshipType: this.context.relationshipType,
          relationshipExternalId: contact.relationshipExternalId,
          propertyExternalId: contact.propertyExternalId,
          unitExternalId: contact.unitExternalId,
          isActive: contact.isActive,
        })
      }

      // Merge non-email/phone fields from non-base contacts
      if (contact !== baseContact) {
        this.mergeNonContactFields(finalContact, contact)
      }
    }

    // Assign primary and alternate emails
    const emailArray = Array.from(allEmails)
    // Keep base contact's primary email if it exists, otherwise first email
    if (baseContact.primaryEmail) {
      finalContact.primaryEmail = baseContact.primaryEmail
      finalContact.alternateEmails = emailArray.filter(
        e => e !== baseContact.primaryEmail,
      )
    } else if (emailArray.length > 0) {
      finalContact.primaryEmail = emailArray[0]
      finalContact.alternateEmails = emailArray.slice(1)
    }

    // Assign primary and alternate phones
    const phoneArray = Array.from(allPhones)
    // Keep base contact's primary phone if it exists, otherwise first phone
    if (baseContact.primaryPhoneNumber) {
      finalContact.primaryPhoneNumber = baseContact.primaryPhoneNumber
      finalContact.alternatePhoneNumbers = phoneArray
        .filter(p => p !== baseContact.primaryPhoneNumber)
        .map(phone => ({
          number: phone,
          type: baseContact.primaryPhoneNumberType || 'phone',
        }))
    } else if (phoneArray.length > 0) {
      finalContact.primaryPhoneNumber = phoneArray[0]
      finalContact.alternatePhoneNumbers = phoneArray.slice(1).map(phone => ({
        number: phone,
        type: 'phone',
      }))
    }

    // Generate aliases from non-primary contact names (for merged duplicates)
    const primaryName = getContactDisplayName(finalContact)
    const generatedAliases: string[] = []

    for (const contact of allContacts) {
      if (contact !== baseContact && contact.name) {
        // Use the Name field if provided
        const contactName = contact.name.trim()
        // Only add as alias if the name is different from the primary name
        if (
          contactName &&
          contactName !== primaryName &&
          !generatedAliases.includes(contactName)
        ) {
          generatedAliases.push(contactName)
        }
      }
    }

    // Merge generated aliases with existing aliases (deduped)
    if (generatedAliases.length > 0) {
      finalContact.aliases = [
        ...(finalContact.aliases || []),
        ...generatedAliases,
      ].filter((alias, index, self) => self.indexOf(alias) === index)
    }

    return {
      finalContact,
      relationships: allRelationships,
    }
  }

  /**
   * Merge non-email/phone fields from source to target
   * Only merges specific text and array fields that aren't contact methods
   */
  private mergeNonContactFields(
    target: Partial<ContactPropertyRelationshipRecord>,
    source: Partial<ContactPropertyRelationshipRecord>,
  ): void {
    // Merge simple string fields (only update if source has value and target doesn't)
    if (source.firstName) target.firstName = source.firstName
    if (source.lastName) target.lastName = source.lastName
    if (source.companyName) target.companyName = source.companyName
    if (source.externalId && !target.externalId)
      target.externalId = source.externalId
    if (source.city && !target.city) target.city = source.city

    // Merge array fields
    if (source.aliases && source.aliases.length > 0) {
      target.aliases = [...(target.aliases || []), ...source.aliases].filter(
        (alias, index, self) => self.indexOf(alias) === index,
      ) // dedupe
    }
  }

  /**
   * Find new relationships that need to be added to an existing contact
   */
  private findNewRelationships(
    group: UnifiedContactGroup,
  ): RelationshipToCreate[] {
    const existingContact = group.existingContact!
    const uploadContacts = group.contacts.filter(c =>
      this.isUploadContact(c),
    ) as UploadContact[]

    // Dedup keys for old-format (relationshipExternalId) and new-format (property+unit composite)
    const existingRelationshipIds = new Set(
      existingContact.relationships
        .map(rel => rel.relationshipExternalId)
        .filter(Boolean),
    )
    const existingPropertyUnitKeys = new Set(
      existingContact.relationships
        .map(
          rel => `${rel.propertyExternalId ?? ''}|${rel.unitExternalId ?? ''}`,
        )
        .filter(key => key !== '|'),
    )

    // Create relationships for upload contacts that don't already exist
    const relationships: RelationshipToCreate[] = []

    for (const uploadContact of uploadContacts) {
      if (
        this.context.relationshipType &&
        (uploadContact.relationshipExternalId ||
          uploadContact.propertyExternalId ||
          uploadContact.unitExternalId)
      ) {
        if (uploadContact.relationshipExternalId) {
          // Old-format: dedup by relationshipExternalId
          if (existingRelationshipIds.has(uploadContact.relationshipExternalId))
            continue
        } else {
          // New-format: dedup by property+unit combination
          const key = `${uploadContact.propertyExternalId ?? ''}|${uploadContact.unitExternalId ?? ''}`
          if (key !== '|' && existingPropertyUnitKeys.has(key)) continue
        }
        relationships.push({
          relationshipType: this.context.relationshipType,
          relationshipExternalId: uploadContact.relationshipExternalId,
          propertyExternalId: uploadContact.propertyExternalId,
          unitExternalId: uploadContact.unitExternalId,
          isActive: uploadContact.isActive,
        })
      }
    }

    return relationships
  }

  /**
   * Resolve field value based on resolution rule
   */
  private resolveField = (
    resolution: FieldResolution,
    existingValue: FieldValue,
    newValue: FieldValue,
  ): FieldValue => {
    switch (resolution) {
      case 'keep_existing':
        return existingValue || newValue

      case 'use_new':
        return newValue || existingValue

      case 'merge_arrays':
        return this.mergeArrays(existingValue, newValue)

      default:
        return newValue || existingValue
    }
  }

  /**
   * Merge and deduplicate arrays
   * @param existing - The existing array
   * @param incoming - The incoming array
   * @returns The merged array
   */
  private mergeArrays(existing: FieldValue, incoming: FieldValue): FieldValue {
    const existingArray = Array.isArray(existing)
      ? existing
      : existing
        ? [existing]
        : []
    const incomingArray = Array.isArray(incoming)
      ? incoming
      : incoming
        ? [incoming]
        : []

    // Use Set for efficient deduplication
    return [...new Set([...existingArray, ...incomingArray])] as FieldValue
  }

  /**
   * Check if a value has actually changed
   */
  private hasChanged = (
    oldValue: FieldValue,
    newValue: FieldValue,
  ): boolean => {
    // Handle arrays
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      // More efficient array comparison
      if (oldValue.length !== newValue.length) return true
      const sortedOld = [...oldValue].sort()
      const sortedNew = [...newValue].sort()
      return !sortedOld.every((val, index) => val === sortedNew[index])
    }

    // Handle primitives
    return oldValue !== newValue
  }

  /**
   * Generate unified CRM resolution actions for API batching
   * Excludes skipped actions since they don't require API calls
   */
  private generateCrmContactResolutionActions(actionGroups: {
    newContacts: ContactCreateAction[]
    duplicateCreates: ContactCreateAction[]
    updates: ContactUpdateAction[]
  }): CrmContactResolutionAction[] {
    const actions: CrmContactResolutionAction[] = []
    let order = 1

    // New contacts first (order 1-N)
    actionGroups.newContacts.forEach(action => {
      actions.push({
        id: `create-new-${crypto.randomUUID()}`,
        type: 'create_contact',
        order: order++,
        action,
      })
    })

    // Duplicate creates next (order N+1-M)
    actionGroups.duplicateCreates.forEach(action => {
      actions.push({
        id: `create-dup-${crypto.randomUUID()}`,
        type: 'create_contact',
        order: order++,
        action,
      })
    })

    // Updates last (order M+1-K) - includes both field and relationship updates
    actionGroups.updates.forEach(action => {
      actions.push({
        id: `update-${action.targetContactId}`,
        type: 'update_contact',
        order: order++,
        action,
      })
    })

    return actions
  }

  /**
   * Type guard to check if contact is from upload
   */
  private isUploadContact = (
    contact: GroupMemberContact,
  ): contact is UploadContact => {
    return 'relationshipExternalId' in contact && !('relationships' in contact)
  }
}
